<template>
  <div class="tooltip-overlay" v-if="toolId" :style="positionStyle">
    <div class="tooltip-content" :class="{ 'coming-soon': isDisabled, 'no-wrap': toolId === 'generateBackground' }">
      <div class="tooltip-header">
        <h3>{{ name }}</h3>
        <div v-if="isDisabled" class="coming-soon-badge">Coming Soon!</div>
      </div>
      <!-- Lottie animation container - positioned under title but above description -->
      <div v-if="hasAnimation" ref="animationContainer" class="tooltip-animation-container"></div>
      <p class="tooltip-description" :class="{ 'no-wrap': toolId === 'generateBackground' }">{{ description }}</p>
    </div>
  </div>
</template>

<script>
import lottie from 'lottie-web';

export default {
  name: 'ToolTooltip',
  props: {
    toolId: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    positionStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      animation: null
    };
  },
  computed: {
    hasAnimation() {
      return this.toolId && this.getAnimationData()[this.toolId] && !this.isDisabled;
    }
  },
  watch: {
    toolId(newValue, oldValue) {
      // Destroy previous animation if any
      if (this.animation) {
        this.animation.destroy();
        this.animation = null;
      }

      // If we have a new toolId and it has an animation, load it
      if (newValue && this.hasAnimation) {
        this.$nextTick(() => {
          this.loadAnimation();
        });
      }
    }
  },
  methods: {
    getAnimationData() {
      return {
        removeObjects: require('@/assets/gif/Object Removal.json'),
        removeBackground: require('@/assets/gif/Bckg Removal.json'),
        outpainting: require('@/assets/gif/Img Expansion.json'),
        generate: require('@/assets/gif/Gen Fill.json')
      };
    },
    loadAnimation() {
      if (!this.$refs.animationContainer) return;

      const animationData = this.getAnimationData()[this.toolId];
      if (!animationData) return;

      this.animation = lottie.loadAnimation({
        container: this.$refs.animationContainer,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice'
        }
      });

      // Simple approach - add a background with border radius instead
      this.$refs.animationContainer.style.backgroundColor = '#f9f9f9';
      this.$refs.animationContainer.style.borderRadius = '8px';
    }
  },
  mounted() {
    if (this.hasAnimation) {
      this.loadAnimation();
    }
  },
  beforeDestroy() {
    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.tooltip-overlay {
  position: absolute;
  z-index: 1000;
  pointer-events: none;
  width: auto;
  height: auto;
}

.tooltip-content {
  background: white;
  border-radius: 8px;
  padding: 16px;
  min-width: 260px;
  max-width: 320px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.coming-soon {
    border: 2px dashed #ccc;
    background: #f9f9f9;
  }

  &.no-wrap {
    white-space: nowrap;
    width: auto;
    min-width: auto;
    max-width: none;
    padding: 16px 24px;
  }
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  h3 {
    @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }
}

.coming-soon-badge {
  background: #fecb2f;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  margin-left: 12px;
}

.tooltip-description {
  @include inter-font($size: 14px, $color: var(--secondary-text-color));
  margin: 0;
  line-height: 1.5;

  &.no-wrap {
    white-space: nowrap;
  }
}

.tooltip-animation-container {
  margin: 8px 0;
  width: 100%;
  overflow: hidden;
  height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
}
</style>
